import {useCallback, useContext} from 'react'
import { useToaster } from 'ui-seeker/core'
import { postSubmit, getSubmit } from 'ui-seeker/utils'

import { getOptionValues } from 'app/components/commonFilters/hooks'
import { ALERTS, BOOKING_SITES } from 'app/@common/constants'
import { AlertsContext } from '../index'


export default function useAlerts() {

    const { state, setState } = useContext(AlertsContext)
	const {
		showAlertsDrawer, alertsNotifications, totalAlertsNotifications, alerts, isLoadingAlerts, isLoadingTable,
		ikey, checkElements, cols, showNotificationsDrawer, dataRow, openNotifications, alertsOccupancy,
	} = state

	const { showToaster } = useToaster()

    const updateState = useCallback(newState => {
        setState(state => ({ ...state, ...newState}))
	}, [setState])

	const setAlertsNotifications = useCallback(newState => {
        updateState({ alertsNotifications: newState })
    }, [updateState])

	const setTotalAlertsNotifications = useCallback(newState => {
        updateState({ totalAlertsNotifications: newState })
    }, [updateState])

	const setShowAlertsDrawer = useCallback(newState => {
		updateState({ showAlertsDrawer: newState })
	}, [updateState])

	const setShowNotificationsDrawer = useCallback(newState => {
		updateState({ showNotificationsDrawer: newState })
	}, [updateState])

	const setAlerts = useCallback((data, isLoading=true) => {
        updateState({ alerts: data, isLoadingAlerts: isLoading })
    }, [updateState])

	const setLoadingTable = useCallback(newState => {
		updateState({ isLoadingTable: newState })
	}, [updateState])

	const setIkey = useCallback(newState => {
        updateState({ ikey: newState })
    }, [updateState])

    const setCheckElements = useCallback(newState => {
		updateState({ checkElements: newState })
	}, [updateState])

	const setCols = useCallback(newState => {
		updateState({ cols: newState })
	}, [updateState])

	const setDataRow = useCallback(newState => {
		updateState({ dataRow: newState })
	}, [updateState])

	const setOpenNotifications = useCallback(newState => {
        updateState({ openNotifications: newState })
    }, [updateState])

	const setAlertsOccupancy = useCallback(op => {
		updateState({ alertsOccupancy: getOptionValues(op) })
	}, [updateState])

    const getCheckElements = data => {

		const bookingSites = [BOOKING_SITES.APP, BOOKING_SITES.GENIUS, BOOKING_SITES.MOBILE, BOOKING_SITES.COM, BOOKING_SITES.MX,
			BOOKING_SITES.CN, BOOKING_SITES.BASIC, BOOKING_SITES.CORE, BOOKING_SITES.PAY, BOOKING_SITES.BR, BOOKING_SITES.PT,
			BOOKING_SITES.US, BOOKING_SITES.UK]

		return [{
			label: 'Cancellation Policy',
			isActive: false,
			visible: true,
			styleTag: {
				backgroundColor: '#12A3B9',
				color: '#FFF'
			}
		}, {
			label: 'Meal plan',
			isActive: false,
			visible: true,
			styleTag: {
				backgroundColor: '#E0FF4F',
				color: '#000'
			}
		}, {
			label: 'FRA',
			description: 'Few Rooms Available',
			isActive: false,
			visible: data[0]?.tp?.toString() === ALERTS.COMPETITION,
			styleTag: {
				backgroundColor: '#80DB8F',
				color: '#000'
			}
		}, {
			label: 'Loyalty',
			description: 'Displays that a member rate is available (shown rates may or may not apply then discount)',
			isActive: false,
			visible: data[0]?.tp?.toString() === ALERTS.COMPETITION,
			styleTag: {
				backgroundColor: '#FFE45E',
				color: '#000'
			}
		}, {
			label: '3rd Supplier',
			description: 'Displays rates through third party suppliers',
			isActive: false,
			visible: data.some(a => bookingSites.includes(a.id_site)),
			styleTag: {
				backgroundColor: '#F79F79',
				color: '#000',
				display: data.some(a => bookingSites.includes(a.id_site)) ? 'inline-block' : 'none'
			}
		}, {
			label: 'Device',
			isActive: false,
			visible: data[0]?.tp?.toString() === ALERTS.COMPETITION,
			styleTag: {
				backgroundColor: '#00D1C0',
				color: '#FFF',
				display: !data.some(a => bookingSites.includes(a.id_site)) ? 'inline-block' : 'none'
			}
		}, {
			label: 'Booking Pay',
			description: 'Special deals for non direct payments',
			isActive: false,
			visible: data.some(a => bookingSites.includes(a.id_site)),
			styleTag: {
				backgroundColor: '#2474B2',
				color: '#FFF',
				display: data.some(a => bookingSites.includes(a.id_site)) ? 'inline-block' : 'none'
			},
		}, {
			label: 'Room name',
			isActive: false,
			visible: true,
			styleTag: {
				backgroundColor: '#DBC9FF',
				color: '#000'
			}
		}]
	}

    const getAlerts = (alertType='competition') => {
        setLoadingTable(true)

		getSubmit(`${ALERTS.LIST}/${alertType}`).then(result => {
			setAlerts(result, false)
            setLoadingTable(false)

        }).catch(res => {
			setAlerts([], false)
            setLoadingTable(false)
            showToaster.error(res.error)
        })
	}

	const getAllIkeys = () => {
        getSubmit(`${ALERTS.IKEYS_LIST}`).then(result => {
            setIkey(result)
        }).catch(res => {
            showToaster.error(res.error)
        })
    }

	const getAlertsNotifications = (dataFilters={}) => {
		setLoadingTable(true)

		if (!dataFilters.noNights) {
			postSubmit(ALERTS.NOTIFICATIONS_LIST, {...dataFilters}).then(data => {
				setCheckElements(getCheckElements(data))
				setAlertsNotifications(data)
				setLoadingTable(false)
			}).catch(res => {
				showToaster.error(res.statusText)
				setLoadingTable(false)
			})
		} else setLoadingTable(false)
    }

	const getTotalAlertsNotifications = monthYear => {
		postSubmit(ALERTS.COUNT, { dateRange: monthYear }).then(data => {
			setTotalAlertsNotifications(data)
		}).catch(res => {
			showToaster.error(res.statusText)
		})
    }

	const addDays = (date, days) => {
        const newDate = new Date(date)
        newDate.setDate(date.getDate() + days)
        return newDate
    }

    const getMaxDate = (numberOfDays , startDate = new Date()) => addDays(startDate, parseInt(numberOfDays))

	return {
		showAlertsDrawer, alertsNotifications, totalAlertsNotifications, isLoadingTable, isLoadingAlerts, ikey, alerts,
		checkElements, cols, showNotificationsDrawer, dataRow, openNotifications, alertsOccupancy,
		updateState, setShowAlertsDrawer, setAlertsNotifications, getAlertsNotifications, getTotalAlertsNotifications,
		getAlerts, getAllIkeys, setIkey, setLoadingTable, setCheckElements, getCheckElements, addDays, getMaxDate,
		setCols, setShowNotificationsDrawer, setDataRow, setOpenNotifications, setTotalAlertsNotifications,
		setAlertsOccupancy
	}
}